import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { EditorialPageHeader } from "@/shared/components/ui/EditorialPageHeader";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { ChesslaLoadingMark } from "@/shared/components/ChesslaLoadingMark";
import { deleteSession, fetchSessions, type SessionListItem } from "./chat-list.api";
import { sessionTitle } from "./chat-session-presentation";
import { SingleGameAnalysisModal } from "@/features/analysis";
import { layoutTokens } from "@/shared/theme";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { resolveAnalysisAction } from "@/features/entitlements/entitlement-policy";

const sessionDate = (value: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(new Date(value))
    .toUpperCase();

export const ChatListPage = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SessionListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [analysisGameId, setAnalysisGameId] = useState<string | null>(null);
  const entitlementSnapshot = useEntitlementsStore((state) => state.snapshot);
  const openAnalysis = (game: NonNullable<SessionListItem["game"]>) => {
    const gate = resolveAnalysisAction(entitlementSnapshot, game.id, game.analyzed, game.exactTimeControl);
    if (gate.allowed) setAnalysisGameId(game.id);
    else if (gate.cta) void navigate(gate.cta === "manage_trial" ? "/subscription#trial" : "/subscription");
  };

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void fetchSessions()
      .then((value) => {
        if (active) setSessions(value);
      })
      .catch(() => {
        if (active) setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    setDeleteFailed(false);
    try {
      await deleteSession(pendingDelete.id);
      setSessions((current) => current.filter(({ id }) => id !== pendingDelete.id));
      setPendingDelete(null);
    } catch {
      setDeleteFailed(true);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <ChesslaLoadingMark fillContent page="Conversations" label="Reading your coaching record" />;

  return (
    <Box
      component="main"
      data-testid="nikola-conversations"
      data-tour="chats-workspace"
      sx={{
        width: "100%",
        maxWidth: layoutTokens.workspaceMaxWidth,
        mx: "auto",
        px: layoutTokens.workspacePagePadding,
        py: { xs: 2.5, sm: 4 },
      }}
    >
      <EditorialPageHeader
        kicker="YOUR COACHING RECORD"
        title={
          <>
            Nikola
            <br />
            conversations.
          </>
        }
        metadata={
          <>
            <span>
              {sessions.length} CONVERSATION{sessions.length === 1 ? "" : "S"}
            </span>
            <span>·</span>
            <span>YOUR GAMES. VERIFIED EVIDENCE. ONE COACH.</span>
          </>
        }
        action={
          <Button
            data-tour="chats-new"
            startIcon={<AddIcon />}
            onClick={() => void navigate("/chat?new=1")}
            sx={{
              minHeight: 52,
              px: 2.25,
              borderRadius: 0,
              bgcolor: "primary.main",
              color: "background.default",
              fontWeight: 900,
              "&:hover": { bgcolor: "secondary.main" },
            }}
          >
            NEW CONVERSATION
          </Button>
        }
      />

      <Box
        component="section"
        data-tour="chats-history"
        aria-labelledby="conversation-index-heading"
        sx={{ borderTop: "3px solid", borderColor: "primary.main" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            id="conversation-index-heading"
            component="h2"
            sx={{ fontSize: ".68rem", fontWeight: 900, letterSpacing: ".11em" }}
          >
            CONVERSATION INDEX
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".62rem", fontWeight: 800 }}>NEWEST FIRST</Typography>
        </Box>

        {failed && (
          <Box
            role="alert"
            sx={{ minHeight: 180, display: "grid", placeItems: "center", gap: 1.5, textAlign: "center" }}
          >
            <Typography>Could not load your conversations.</Typography>
            <Button
              onClick={() => {
                load();
              }}
              sx={{ minHeight: 44, borderRadius: 0 }}
            >
              TRY AGAIN
            </Button>
          </Box>
        )}
        {!failed && sessions.length === 0 && (
          <Box
            sx={{
              minHeight: 260,
              display: "grid",
              placeItems: "center",
              py: 5,
              textAlign: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box>
              <Typography
                component="h3"
                sx={{
                  fontSize: { xs: "1.8rem", sm: "2.6rem" },
                  fontWeight: 900,
                  letterSpacing: "-.055em",
                  lineHeight: 1,
                }}
              >
                Your first useful question starts here.
              </Typography>
              <Typography sx={{ maxWidth: 500, mt: 1.5, color: "text.secondary" }}>
                Ask about one game, one decision, or what your recent evidence says you should work on.
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => void navigate("/chat?new=1")}
                sx={{ minHeight: 48, mt: 2, borderRadius: 0 }}
              >
                ASK NIKOLA
              </Button>
            </Box>
          </Box>
        )}

        {!failed && sessions.length > 0 && (
          <Box component="ol" sx={{ p: 0, m: 0, listStyle: "none" }}>
            {sessions.map((session, index) => {
              const title = sessionTitle(session.topic, session.game);
              const analysisGate = session.game
                ? resolveAnalysisAction(
                    entitlementSnapshot,
                    session.game.id,
                    session.game.analyzed,
                    session.game.exactTimeControl,
                  )
                : null;
              return (
                <Box
                  component="li"
                  key={session.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "44px minmax(0,1fr) 44px", sm: "64px minmax(0,1fr) auto 48px" },
                    alignItems: "stretch",
                    minHeight: { xs: 112, sm: 128 },
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "rgba(240,236,226,.035)" },
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      borderRight: "1px solid",
                      borderColor: "divider",
                      color: "secondary.main",
                      fontSize: { xs: ".72rem", sm: ".85rem" },
                      fontWeight: 900,
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </Box>
                  <Box
                    sx={{ minWidth: 0, display: "grid", alignContent: "center", px: { xs: 1.25, sm: 2.25 }, py: 1.25 }}
                  >
                    <Button
                      aria-label={`Open ${title}`}
                      onClick={() => void navigate(`/chat?sessionId=${encodeURIComponent(session.id)}`)}
                      sx={{
                        minWidth: 0,
                        minHeight: 44,
                        p: 0,
                        justifyContent: "space-between",
                        gap: 1.5,
                        borderRadius: 0,
                        color: "text.primary",
                        textAlign: "left",
                        "&:hover": { bgcolor: "transparent" },
                      }}
                    >
                      <Typography
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: { xs: ".9rem", sm: "1.15rem" },
                          fontWeight: 900,
                          letterSpacing: "-.02em",
                          textTransform: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {title}
                      </Typography>
                      <ArrowForwardIcon
                        aria-hidden
                        sx={{ display: { xs: "none", sm: "block" }, flex: "0 0 auto", color: "secondary.main" }}
                      />
                    </Button>
                    <Typography
                      sx={{ color: "text.secondary", fontSize: ".61rem", fontWeight: 800, letterSpacing: ".055em" }}
                    >
                      {sessionDate(session.createdAt)}
                    </Typography>
                    {session.game && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.65, flexWrap: "wrap" }}>
                        <Typography sx={{ color: "text.secondary", fontSize: ".58rem", fontWeight: 800 }}>
                          {session.game.timeControl.toUpperCase()} · {session.game.result.toUpperCase()} ·{" "}
                          {sessionDate(session.game.playedAt)}
                        </Typography>
                        {session.game.analyzed ? (
                          <Button
                            component={RouterLink}
                            to={`/games/${encodeURIComponent(session.game.id)}`}
                            aria-label={`Open your game against ${session.game.opponent}`}
                            sx={{ minHeight: 44, p: 0, borderRadius: 0, color: "secondary.main", fontSize: ".61rem" }}
                          >
                            OPEN GAME →
                          </Button>
                        ) : (
                          <Button
                            disabled={!analysisGate?.allowed && !analysisGate?.cta}
                            onClick={() => openAnalysis(session.game!)}
                            aria-label={`Analyze game against ${session.game.opponent}`}
                            sx={{ minHeight: 44, p: 0, borderRadius: 0, color: "secondary.main", fontSize: ".61rem" }}
                          >
                            {analysisGate?.allowed
                              ? "ANALYZE GAME →"
                              : analysisGate?.cta === "manage_trial"
                                ? "MANAGE TRIAL →"
                                : analysisGate?.cta === "join_club"
                                  ? "JOIN CLUB →"
                                  : "CHECKING…"}
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: { xs: "none", sm: "grid" }, placeItems: "center", px: 1.5 }}>
                    <StatusBadge
                      label={session.isActive ? "ACTIVE" : "ARCHIVED"}
                      tone={session.isActive ? "active" : "neutral"}
                    />
                  </Box>
                  <IconButton
                    aria-label={`Delete ${title}`}
                    onClick={() => {
                      setDeleteFailed(false);
                      setPendingDelete(session);
                    }}
                    sx={{
                      width: 44,
                      minHeight: 44,
                      alignSelf: "center",
                      borderRadius: 0,
                      color: "text.secondary",
                      "&:hover": { color: "secondary.main", bgcolor: "rgba(255,61,0,.08)" },
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog
        open={pendingDelete != null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        PaperProps={{ sx: { width: "min(92vw,460px)", borderRadius: 0, border: "1px solid", borderColor: "divider" } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>DELETE CONVERSATION?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            This removes the conversation and its messages. Your games and analysis stay intact.
          </Typography>
          {deleteFailed && (
            <Typography role="alert" sx={{ mt: 1.5, color: "error.main" }}>
              Could not delete this conversation. Try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={deleting} onClick={() => setPendingDelete(null)} sx={{ minHeight: 44, borderRadius: 0 }}>
            KEEP IT
          </Button>
          <Button
            disabled={deleting}
            onClick={() => void confirmDelete()}
            color="error"
            sx={{ minHeight: 44, borderRadius: 0 }}
          >
            {deleting ? "DELETING…" : "DELETE"}
          </Button>
        </DialogActions>
      </Dialog>
      <SingleGameAnalysisModal
        gameId={analysisGameId}
        onClose={() => setAnalysisGameId(null)}
        onComplete={({ gameId }) => {
          setSessions((current) =>
            current.map((session) =>
              session.game?.id === gameId ? { ...session, game: { ...session.game, analyzed: true } } : session,
            ),
          );
          setAnalysisGameId(null);
          void navigate(`/games/${encodeURIComponent(gameId)}`);
        }}
      />
    </Box>
  );
};
